/** Prepares images for the Magic Eraser and calls the cloud API. Photos are downscaled to at most 1024px before leaving the device. */

export const MAX_ERASE_EDGE = 1024;

export class MagicEraserError extends Error {
  constructor(message: string, readonly kind: 'offline' | 'not-allowed' | 'blocked' | 'tired' | 'unknown') {
    super(message);
    this.name = 'MagicEraserError';
  }
}

export interface Stroke {
  points: { x: number; y: number }[];
  /** Brush size relative to the image width (0..1). */
  size: number;
}

function toBase64(dataUrl: string): string {
  return dataUrl.slice(dataUrl.indexOf(',') + 1);
}

async function loadBitmap(blob: Blob): Promise<ImageBitmap> {
  return createImageBitmap(blob);
}

function scaledSize(width: number, height: number): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= MAX_ERASE_EDGE) return { width, height };
  const scale = MAX_ERASE_EDGE / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

function drawStrokes(context: CanvasRenderingContext2D, strokes: Stroke[], width: number, height: number): void {
  context.lineCap = 'round';
  context.lineJoin = 'round';
  for (const stroke of strokes) {
    context.lineWidth = Math.max(4, stroke.size * width);
    context.strokeStyle = '#fff';
    context.fillStyle = '#fff';
    const first = stroke.points[0];
    if (!first) continue;
    if (stroke.points.length === 1) {
      context.beginPath();
      context.arc(first.x * width, first.y * height, context.lineWidth / 2, 0, Math.PI * 2);
      context.fill();
      continue;
    }
    context.beginPath();
    context.moveTo(first.x * width, first.y * height);
    for (const point of stroke.points.slice(1)) context.lineTo(point.x * width, point.y * height);
    context.stroke();
  }
}

/** Builds the downscaled photo (JPEG) and the scribble mask (PNG, white on black) as base64 strings. */
export async function buildErasePayload(photo: Blob, strokes: Stroke[]): Promise<{ image: string; imageMimeType: string; mask: string }> {
  const bitmap = await loadBitmap(photo);
  const { width, height } = scaledSize(bitmap.width, bitmap.height);
  const photoCanvas = document.createElement('canvas');
  photoCanvas.width = width;
  photoCanvas.height = height;
  const photoContext = photoCanvas.getContext('2d');
  if (!photoContext) throw new MagicEraserError('This device cannot prepare the picture.', 'unknown');
  photoContext.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = width;
  maskCanvas.height = height;
  const maskContext = maskCanvas.getContext('2d');
  if (!maskContext) throw new MagicEraserError('This device cannot prepare the picture.', 'unknown');
  maskContext.fillStyle = '#000';
  maskContext.fillRect(0, 0, width, height);
  drawStrokes(maskContext, strokes, width, height);

  return {
    image: toBase64(photoCanvas.toDataURL('image/jpeg', 0.9)),
    imageMimeType: 'image/jpeg',
    mask: toBase64(maskCanvas.toDataURL('image/png')),
  };
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

/** Sends the photo and mask to the eraser API and returns the edited photo. */
export async function eraseFromPhoto(photo: Blob, strokes: Stroke[]): Promise<Blob> {
  const payload = await buildErasePayload(photo, strokes);
  let response: Response;
  try {
    response = await fetch('/api/erase', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new MagicEraserError("The eraser fairy can't fly right now. Check the internet and try again.", 'offline');
  }
  if (!response.ok) {
    let message = 'The eraser could not fix this picture. Try a different picture!';
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch { /* keep fallback */ }
    if (response.status === 401 || response.status === 403) throw new MagicEraserError(message, 'not-allowed');
    if (response.status === 429) throw new MagicEraserError(message, 'tired');
    if (response.status === 422) throw new MagicEraserError(message, 'blocked');
    throw new MagicEraserError(message, 'unknown');
  }
  const body = (await response.json()) as { image: string; mimeType: string };
  return base64ToBlob(body.image, body.mimeType);
}
